import { ApiError } from "@softmax/observatory-client/transport";
import { readResult } from "@/data/read-result";
import { getActualViewer } from "@/data/viewer";
import { requireGameContext } from "@/features/game/data";
import { readPublishedStorePage } from "@/features/preview/data";
import { createPlayReader } from "../data/play-content";
import { createOnePagerReader } from "./one-pager-data";
import { onePagerAddress } from "./one-pager-address";

type PageInput = { href: string };
type PageRead<T = PageInput> = { data: T; signal?: AbortSignal | undefined };

export function getOnePagerPersonal({
  data,
  signal,
}: PageRead<PageInput & { viewerId: string }>) {
  return readResult(async () => {
    const context = await requireGameContext({ href: data.href, signal });
    const play = createPlayReader({ context, signal });
    const viewer = await getActualViewer({ signal });

    if (viewer?.subject_id !== data.viewerId)
      throw new ApiError("Your session has changed. Refresh to continue.", 401);

    const shell = await play.shell(onePagerAddress(data.href));

    if (!shell) throw new ApiError("This league is no longer available.", 404);

    return {
      shell: { league: shell.league, divisionId: shell.divisionId },
      data: await createOnePagerReader({ api: play.api, signal }).loadOnePager(
        shell,
      ),
    };
  });
}

export function getOnePagerBoard({ data, signal }: PageRead) {
  return readResult(async () => {
    const context = await requireGameContext({ href: data.href, signal });
    const play = createPlayReader({ context, signal });
    const shell = await play.featuredPublicShell(onePagerAddress(data.href));

    if (!shell) throw new ApiError("This league is no longer available.", 404);

    return {
      shell,
      data: await createOnePagerReader({
        api: play.api,
        signal,
      }).loadPublicBoard(shell),
    };
  });
}

export function getOnePagerRecommendations({
  data,
  signal,
}: PageRead<PageInput & { viewerId: string | null }>) {
  return readResult(async () => {
    const context = await requireGameContext({ href: data.href, signal });
    const play = createPlayReader({ context, signal });

    if (data.viewerId !== null) {
      const viewer = await getActualViewer({ signal });

      if (viewer?.subject_id !== data.viewerId)
        throw new ApiError(
          "Your session has changed. Refresh to continue.",
          401,
        );
    }

    const shell =
      data.viewerId === null
        ? await play.featuredPublicShell(onePagerAddress(data.href))
        : await play.shell(onePagerAddress(data.href));

    if (!shell) throw new ApiError("This league is no longer available.", 404);

    return {
      divisionId: shell.divisionId,
      programs: await createOnePagerReader({
        api: play.api,
        signal,
      }).loadRecommended(shell),
    };
  });
}

export function getOnePagerDecoration({ data, signal }: PageRead) {
  return readResult(async () => {
    const context = await requireGameContext({ href: data.href, signal });
    const page = await readPublishedStorePage({
      slug: context.corpusSlug.replace(/\/.*$/, ""),
      signal,
    });
    return page ? { icon: page.content.icon, blurb: page.content.blurb } : null;
  });
}
